import { motion } from "framer-motion";
import Fetcher from "../hooks/Fetcher";
import BlockRightImage from "../components/BlockRightImage";
import Accordion from "../components/Accordion";
import {
  CalendarDays,
  Factory,
  CircleUserRound,
  FileBox,
  MicVocal,
  ShieldIcon,
  UserCheck,
  UserStar,
  Vote,
  Computer,
} from "lucide-react";
import { Inscripcion } from "../components/Inscripcion";

const url =
  "https://eventos.utpl.edu.ec/jsonapi/node/congresos/c68f2c0b-d1bc-458f-ad22-4e1f631c653a" +
  "?include=field_imagen2.field_media_image" +
  "&fields[node--congresos]=title,field_texto_portada,field_imagen2,field_bloque1,field_bloque2,field_bloque3,field_bloque4,field_bloque5,field_bloque6," +
  "field_bloque7,field_bloque8,field_bloque9,field_bloque10,field_bloque11,field_bloque12,field_bloque13,field_bloque14,field_bloque15,field_bloque16,field_bloque17," +
  "field_bloque18,field_bloque19,field_bloque20,field_bloque21,field_bloque22,field_bloque23";
const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.15,
    },
  },
};

const cardVariants = {
  hidden: {
    opacity: 0,
    y: 40,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut",
    },
  },
};

function Conferencia() {
  const DataDisplay = ({ data }) => {
    const bloques = [
      { html: data?.bloque4, icon: Vote },
      { html: data?.bloque5, icon: Computer },
      { html: data?.bloque6, icon: UserStar },
    ];

    const BASE_URL = "https://eventos.utpl.edu.ec";

    const processContent = (content) => {
      if (!content) return "";

      return content.replace(/src="\/(.*?)"/g, `src="${BASE_URL}/$1"`);
    };

    return (
      <div>
        <section
          id="inicio"
          className="relative overflow-hidden bg-gradient-to-br from-[#00365d] via-[#004270] to-[#005d9c] py-10"
        >
          {/* Glow superior */}
          <div className="absolute -top-40 -right-40 h-96 w-96 rounded-full bg-[#febe10]/10 blur-3xl" />

          {/* Glow inferior */}
          <div className="absolute -bottom-56 -left-40 h-[28rem] w-[28rem] rounded-full bg-cyan-400/10 blur-3xl" />

          {/* Patrón de puntos */}
          <div
            className="absolute inset-0 opacity-[0.06]"
            style={{
              backgroundImage: "radial-gradient(#ffffff 1px, transparent 1px)",
              backgroundSize: "22px 22px",
            }}
          />

          <div className="relative max-w-6xl mx-auto px-6">
            <motion.div
              className="h-1 w-20 rounded-full bg-[#febe10] mb-8"
              initial={{ width: 0, opacity: 0 }}
              whileInView={{ width: 80, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            />

            <motion.div
              className="
                [&_h3]:text-[#febe10]
                [&_h3]:font-bold
                    [&_h2]:text-white
             [&_h2]:font-extrabold
                [&_h2]:max-w-4xl
                [&_h2]:text-5xl
                
                [&_h4]:inline-block
                [&_h4]:bg-white
                [&_h4]:backdrop-blur-sm
                [&_h4]:text-[#004270]
                [&_h4]:uppercase
         
                [&_h4]:text-sm
                [&_h4]:font-semibold
                [&_h4]:px-4
                [&_h4]:py-2
                [&_h4]:rounded-full
                [&_h4]:border
                [&_h4]:border-[#febe10]/30
                [&_h4]:shadow-lg
                [&_h4]:shadow-black/30
                [&_h4]:mb-6
                [&_h4]:mr-2
                [&_p]:max-w-2xl
                [&_p]:text-lg
                [&_p]:leading-relaxed
                [&_p]:text-white/90
                [&_p]:mt-4
                [&_p]:mb-10
              "
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{
                duration: 0.8,
                ease: [0.22, 1, 0.36, 1],
              }}
              dangerouslySetInnerHTML={{
                __html: data?.texto_portada || "",
              }}
            />
          </div>
        </section>

        <section className="bg-slate-50 py-24">
          <div className="max-w-6xl px-6 m-auto">
            <BlockRightImage
              src={data.imagen2}
              content={
                <>
                  <div
                    className="pr-0 md:pr-4"
                    dangerouslySetInnerHTML={{
                      __html: data?.bloque2 || "",
                    }}
                  />
                </>
              }
            ></BlockRightImage>
          </div>
        </section>

        <section id="lineas" className="py-24 bg-[#004270]">
          <div className="max-w-7xl mx-auto px-6">
            <div
              className=" text-center [&_h2]:text-white   "
              dangerouslySetInnerHTML={{
                __html: data?.bloque3 || "",
              }}
            />

            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {bloques.map(({ html, icon: Icon }, index) => {
                // Solo renderizamos la tarjeta si hay contenido HTML
                if (!html) return null;

                return (
                  <motion.article
                    key={index}
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    whileHover={{
                      y: -8,
                      transition: { duration: 0.2 },
                    }}
                    className="group rounded-2xl bg-white p-8 shadow-md border border-slate-200 hover:border-[#febe10] hover:shadow-xl transition-all"
                  >
                    <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#004270] text-[#febe10] transition-all group-hover:scale-110 group-hover:rotate-6">
                      <Icon size={30} />
                    </div>

                    <div
                      className="
    
    [&_p]:text-slate-600
    [&_p]:leading-relaxed
    [&_p]:text-lg
    [&_p]:mb-4

    [&_p_strong]:font-semibold
    [&_p_strong]:text-[#004270]
    [&_p_strong]:block
    [&_p_strong]:mb-2

    [&_ul]:list-disc
    [&_ul]:pl-6
    [&_ul]:mb-4

    [&_ol]:list-decimal
    [&_ol]:pl-6
    [&_ol]:mb-4

    [&_li]:text-slate-600
    [&_li]:leading-snug
    [&_li]:text-sm
    [&_li]:mb-2
      [&_h2]:text-lg
    marker:text-[#004270]
  "
                      dangerouslySetInnerHTML={{
                        __html: html,
                      }}
                    />
                  </motion.article>
                );
              })}
            </div>
          </div>
        </section>

        <section id="inscripcion" className="bg-slate-50">
          <Inscripcion
            titulo={data.bloque7}
            bloque1={data.bloque8}
            bloque2={data.bloque9}
          ></Inscripcion>
        </section>
        <section className="max-w-6xl mx-auto px-6 py-24">
          <motion.div
            initial={{ opacity: 0, y: 25 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-14"
          >
            <span className="inline-block bg-[#febe10]/20 text-[#004270] px-4 py-1 rounded-full text-sm font-semibold mb-4">
              Invitados
            </span>

            <h2 className=" font-bold  ">Conferencistas</h2>

            <p>
              Conoce a los expertos nacionales e internacionales que compartirán
              su experiencia y conocimientos durante el evento.
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 btn-primary
      [&_img]:h-80
      [&_img]:w-60
      [&_img]:object-cover
      [&_img]:mx-auto
      [&_img]:rounded-2xl
      [&_img]:shadow-lg
      [&_h2]:text-2xl
      [&_h2]:font-bold
      [&_h2]:text-[#004270]
      [&_h2]:mt-6
      [&_h2]:mb-2
      [&_p]:text-slate-600
      [&_p]:text-sm
      [&_p]:leading-relaxed
    "
          >
            {[data.bloque14, data.bloque15, data.bloque16].map(
              (bloque, index) => (
                <motion.div
                  key={index}
                  variants={cardVariants}
                  whileHover={{
                    y: -8,
                    transition: { duration: 0.25 },
                  }}
                  className="bg-white rounded-3xl p-8 shadow-lg border border-slate-200 overflow-hidden"
                >
                  <div
                    dangerouslySetInnerHTML={{
                      __html: processContent(bloque),
                    }}
                  />
                </motion.div>
              ),
            )}
          </motion.div>
        </section>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="rounded-2xl border border-slate-200 bg-white p-8 shadow-lg max-w-4xl m-auto mb-24"
        >
          <div className="mb-6 flex items-center gap-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#004270]">
              <CalendarDays className="h-7 w-7 text-[#febe10]" />
            </div>
          </div>

          <div
            dangerouslySetInnerHTML={{
              __html: data?.bloque1 || "",
            }}
          />
        </motion.div>
        <section id="call-for-papers" className="py-24  bg-slate-50">
          <div className="max-w-5xl mx-auto px-6">
            <div
              className="      btn-primary "
              dangerouslySetInnerHTML={{
                __html: data?.bloque10 || "",
              }}
            />
          </div>
          <div className="max-w-5xl m-auto mt-10">
            <Accordion
              title="Comité organizador"
              content={
                <>
                  <div
                    className="  btn-primary "
                    dangerouslySetInnerHTML={{
                      __html: data?.bloque11 || "",
                    }}
                  />
                </>
              }
            ></Accordion>{" "}
          </div>
        </section>

        <div className="max-w-6xl m-auto px-6 py-10">
          <div
            className="  text-center"
            dangerouslySetInnerHTML={{
              __html: processContent(data.bloque12),
            }}
          />

      <motion.div
  initial={{ opacity: 0, y: 30 }}
  whileInView={{ opacity: 1, y: 0 }}
  viewport={{ once: true, amount: 0.2 }}
  transition={{ duration: 0.6, ease: "easeOut" }}
  className="
    flex flex-wrap justify-center items-center gap-8

    [&_p]:flex
    [&_p]:flex-wrap
    [&_p]:justify-center
    [&_p]:items-center
    [&_p]:gap-8

    [&_img]:h-16
    md:[&_img]:h-20
    [&_img]:w-auto
    [&_img]:object-contain
    [&_img]:opacity-80
    [&_img]:grayscale
    [&_img]:transition-all
    [&_img]:duration-300
    [&_img:hover]:grayscale-0
    [&_img:hover]:opacity-100
    [&_img:hover]:scale-110
  "
  dangerouslySetInnerHTML={{
    __html: processContent(data.bloque13),
  }}
/>

        </div>
      </div>
    );
  };

  return <Fetcher url={url}>{(data) => <DataDisplay data={data} />}</Fetcher>;
}

export default Conferencia;
